import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * Global image lightbox.
 *
 * Any <img> element inside the app whose `src` starts with `/sketches/`
 * OR that carries a `data-zoomable` attribute becomes clickable.
 * Clicks are intercepted via document-level delegation, so we don't
 * need to touch every page's markup individually. Images that are
 * already wrapped in an <a>, <button> or [data-no-lightbox] parent are
 * left alone.
 *
 * Behaviour:
 *   - cursor:zoom-in and subtle overlay hint on hover ("click to enlarge")
 *   - opens a full-screen modal with the same image at full resolution
 *   - closes on backdrop click, close button or Escape key
 *   - body scroll is locked while the modal is open
 */
const isZoomable = (img) => {
  if (!img || img.tagName !== "IMG") return false;
  // Explicit opt-in via data-zoomable
  if (img.hasAttribute("data-zoomable")) return true;
  // Skip if the image opts out
  if (img.closest("[data-no-lightbox]")) return false;
  // Skip images that are already interactive (nav links, buttons, brand logos)
  if (img.closest("a, button, [role='button']")) return false;
  const src = img.getAttribute("src") || "";
  // Auto-include our editorial content assets: sketches + case photos
  if (src.startsWith("/sketches/")) return true;
  if (src.startsWith("/case-photos/")) return true;
  if (src.startsWith("/photos/")) return true;
  return false;
};

export const ImageLightbox = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState({ src: "", alt: "" });

  const openLightbox = useCallback((src, alt) => {
    setCurrent({ src, alt: alt || "" });
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Delegate clicks + apply cursor styling to zoomable images
  useEffect(() => {
    const decorate = () => {
      document.querySelectorAll("img").forEach((img) => {
        if (isZoomable(img) && !img.dataset.lightboxReady) {
          img.dataset.lightboxReady = "1";
          img.style.cursor = "zoom-in";
          img.setAttribute(
            "title",
            img.getAttribute("title") || "Click to enlarge"
          );
        }
      });
    };

    decorate();
    // Re-decorate when the DOM changes (route navigation, lazy content)
    const mo = new MutationObserver(() => decorate());
    mo.observe(document.body, { childList: true, subtree: true });

    const handleClick = (e) => {
      const img = e.target.closest && e.target.closest("img");
      if (!img) return;
      if (!isZoomable(img)) return;
      e.preventDefault();
      e.stopPropagation();
      openLightbox(img.currentSrc || img.src, img.alt);
    };
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      mo.disconnect();
    };
  }, [openLightbox]);

  // Escape key + body-scroll lock while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!open) return null;

  const modal = (
    <div
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={current.alt || "Enlarged image"}
      onClick={close}
      className="fixed inset-0 z-[999] flex items-center justify-center px-4 md:px-10 py-10 md:py-16 bg-ink/92 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
    >
      <style>
        {`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes lightboxIn { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }`}
      </style>

      <button
        type="button"
        onClick={close}
        data-testid="image-lightbox-close"
        aria-label="Close enlarged image"
        className="absolute top-5 right-5 md:top-8 md:right-8 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-paper/95 hover:bg-paper text-ink flex items-center justify-center font-serif text-2xl leading-none shadow-lg transition-all hover:scale-105"
      >
        &times;
      </button>

      <figure
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center animate-[lightboxIn_0.28s_cubic-bezier(0.16,1,0.3,1)]"
      >
        <img
          src={current.src}
          alt={current.alt || "Enlarged view"}
          draggable={false}
          className="max-w-full max-h-[80vh] w-auto h-auto object-contain bg-paper shadow-[0_20px_80px_rgba(0,0,0,0.5)]"
          style={{ mixBlendMode: "normal" }}
          data-testid="image-lightbox-image"
        />
        {current.alt && (
          <figcaption className="mt-4 md:mt-5 font-hand text-mustard text-base md:text-lg text-center max-w-2xl px-4">
            {current.alt}
          </figcaption>
        )}
      </figure>
    </div>
  );

  return createPortal(modal, document.body);
};
